import { useMutation } from '@apollo/client';
import { AnalyticIdentifier } from '@common/neo.tax';
import { Typography, ControlledInput, Stack, Loading } from '@ui-components/neo.tax';
import { useState } from 'react';
import { Control, FormState, UseFormHandleSubmit, UseFormWatch } from 'react-hook-form';

import { useAuthErrorMessage, useDomainRedirect, useReCaptcha } from '@neo.tax/onboarding/hooks';
import {
  DomainRedirectInterstitial,
  StyledGeneralFormError,
  StyledInfoContainer,
  StyledLinkText,
  SubmitButton,
} from '@neo.tax/onboarding/organisms';
import {
  useAnalyticId,
  validateEmailAddress,
  validateIfExist,
  signInWithEmailAndPassword,
  hasUnverifiedSignedInUser,
  EMAIL_NOT_VERIFIED_REJECTION_REASON,
  NonSsoSignInMethod,
  Path,
  getPath,
  NavLinkTo,
  REPORT_LOGIN_FAILURE,
  ReportLoginFailureMutation,
  ReportLoginFailureMutationVariables,
  ClientReportableLoginMethod,
} from '@neo.tax/shared';
import { isReCaptchaDisabled } from '@neo.tax/shared/utils/environment.utils';

import { LoginFormValues } from './login-form.types';

const INVALID_PASSWORD_ERROR =
  'Invalid username and/or password. If you have previously signed in with a magic link, please use it to sign in.';

export const EmailAndPasswordForm = ({
  control,
  formState,
  handleSubmit,
  setNonSsoSignInMethod,
  watch,
}: {
  control: Control<LoginFormValues, any>;
  handleSubmit: UseFormHandleSubmit<LoginFormValues>;
  formState: FormState<LoginFormValues>;
  setNonSsoSignInMethod: React.Dispatch<React.SetStateAction<NonSsoSignInMethod | null>>;
  watch: UseFormWatch<LoginFormValues>;
}): React.ReactElement => {
  const [reCaptchaToken, setReCaptchaToken] = useState<string | undefined>(isReCaptchaDisabled() ? 'test' : undefined);
  const [isReadyToSubmit, setIsReadyToSubmit] = useState<boolean>(false);
  const emailInputId = useAnalyticId(AnalyticIdentifier.ONBOARDING_EMAIL_INPUT);
  const passwordInputId = useAnalyticId(AnalyticIdentifier.ONBOARDING_PASSWORD_INPUT);
  const logInId = useAnalyticId(AnalyticIdentifier.ONBOARDING_LOGIN_USER);

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useAuthErrorMessage();
  const { isRedirecting, redirectIfSingleTenant } = useDomainRedirect();
  const [reportLoginFailure] = useMutation<ReportLoginFailureMutation, ReportLoginFailureMutationVariables>(
    REPORT_LOGIN_FAILURE,
  );

  const [email, password] = watch(['email', 'password']);

  // Start loading reCAPTCHA script as soon as we render login form
  const { loading, reCaptchaId } = useReCaptcha({
    visible: isReadyToSubmit,
    // We're not using the token atm
    // To assess the reCAPTCHA response token, we need to send it to the backend
    // https://cloud.google.com/recaptcha-enterprise/docs/create-assessment#node.js
    onChecked: (token): void => setReCaptchaToken(token),
  });

  const switchToEmailLink = (): void => {
    setNonSsoSignInMethod(NonSsoSignInMethod.EMAIL_LINK);
  };

  // Firebase settles these before any authenticated request reaches the backend, so the server never
  // observes them unless we report them.
  const reportAttempt = (auditedEmail: string, rejectionReason: string): void => {
    void (async (): Promise<void> => {
      try {
        await reportLoginFailure({
          variables: {
            input: { email: auditedEmail, method: ClientReportableLoginMethod.EmailPassword, rejectionReason },
          },
        });
      } catch {
        // Intentionally swallowed — auditing is best-effort and must not affect login.
      }
    })();
  };

  const onSubmit = async (): Promise<void> => {
    setIsSubmitted(true);
    const trimmedEmail = email.trim().toLowerCase();
    const failure = await signInWithEmailAndPassword(trimmedEmail, password);
    if (failure) {
      // The reason recorded is the real one, so an outage is distinguishable from a wrong password here.
      reportAttempt(trimmedEmail, failure.message);
      setIsReadyToSubmit(false);
      setIsSubmitted(false);
      // Only a rejected credential earns the magic-link hint below. Showing it for a backend that was
      // merely slow would send the user to a link that fails in exactly the same way.
      setErrorMessage(failure.invalidCredentials ? INVALID_PASSWORD_ERROR : failure.message);
      return;
    }
    // The credentials were right but the address is unverified, so useAuthRedirect sends them to the
    // verify-email page. Nothing else records it, which leaves someone who cannot get in looking identical to
    // someone who never tried.
    if (hasUnverifiedSignedInUser()) {
      reportAttempt(trimmedEmail, EMAIL_NOT_VERIFIED_REJECTION_REASON);
    }
  };

  // Before advancing to the reCAPTCHA step, redirect single-tenant-domain users to their instance.
  const handleEmailStageSubmit = async (): Promise<void> => {
    const trimmedEmail = email.trim().toLowerCase();
    if (await redirectIfSingleTenant(trimmedEmail, { forwardEmail: true })) {
      return;
    }
    setIsReadyToSubmit(true);
  };

  if (isRedirecting) {
    return <DomainRedirectInterstitial />;
  }

  if (isReadyToSubmit) {
    return (
      <Stack as="form" direction="column" gap={4} alignItems="center" paddingY={6} onSubmit={handleSubmit(onSubmit)}>
        {!isReCaptchaDisabled() && (
          <>
            {loading ? <Loading height={50} /> : null}
            <div id={reCaptchaId}>
              {/*
            ReCAPTCHA checkbox is rendered as children of element with reCaptchaId
            by the script loaded in useReCaptcha
          */}
            </div>
          </>
        )}
        <SubmitButton
          disabled={!reCaptchaToken}
          variant="primary"
          rounded="regular"
          size="base"
          type="submit"
          id={logInId}
        >
          Sign In
        </SubmitButton>
      </Stack>
    );
  }

  return (
    <Stack as="form" direction="column" paddingY={6} onSubmit={handleSubmit(handleEmailStageSubmit)}>
      <ControlledInput
        control={control}
        name="email"
        rules={{ required: true, validate: validateEmailAddress }}
        id={emailInputId}
        label="Work Email Address"
        placeholder="Work Email Address"
      />
      <ControlledInput
        control={control}
        name="password"
        rules={{ required: true, validate: validateIfExist }}
        id={passwordInputId}
        label="Password"
        type="password"
        placeholder="Password"
      />
      <StyledInfoContainer>
        <Typography as="span" fontSize="sm" fontWeight="normal">
          <Typography as="span" fontSize="sm" fontWeight="medium">
            Forgot your password?
          </Typography>
          <br />
          Sign in with a{' '}
          <button type="button" onClick={switchToEmailLink}>
            <StyledLinkText as="span" fontSize="sm" fontWeight="medium">
              magic link
            </StyledLinkText>
          </button>{' '}
          or{' '}
          <NavLinkTo to={getPath({ path: Path.RESET_PASSWORD })}>
            <StyledLinkText as="span" fontSize="sm" fontWeight="medium">
              reset your password
            </StyledLinkText>
          </NavLinkTo>
        </Typography>
      </StyledInfoContainer>
      <SubmitButton
        disabled={!formState.isValid || isSubmitted || formState.isSubmitting}
        variant="primary"
        rounded="regular"
        size="base"
        type="submit"
        id={logInId}
      >
        Sign In
      </SubmitButton>
      <StyledGeneralFormError error={!!errorMessage}>{errorMessage}</StyledGeneralFormError>
    </Stack>
  );
};
